import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import WizardStepper from "../../components/onboarding/WizardStepper";
import LocationStep from "../../components/onboarding/LocationStep";
import CoverageStep from "../../components/onboarding/CoverageStep";
import PricingStep from "../../components/onboarding/PricingStep";
import DropOffStep from "../../components/onboarding/DropOffStep";
import SellAVehicleStep from "../../components/onboarding/SellAVehicleStep";
import ReviewStep from "../../components/onboarding/ReviewStep";
import { CoverageDistrictState, WIZARD_STEPS, WizardState } from "../../components/onboarding/types";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useOnboarding from "../../data/useOnboarding";
import useCustomer from "../../data/useCustomer";
import usePriceSettings from "../../data/usePriceSettings";
import useNotification from "../../components/notifications/useNotifications";
import { IOnboardingCoverageDistrict } from "../../models/Onboarding";
import {
  DistrictIndex,
  allDistrictsInAreas,
  districtsWithinRadius,
  geocodePostcode,
  loadDistrictIndex,
  lookupDistrictTowns,
  normalisePostcode,
} from "../../utils/postcodeDistricts";

const STEP = Object.fromEntries(WIZARD_STEPS.map((s, i) => [s.id, i])) as Record<(typeof WIZARD_STEPS)[number]["id"], number>;

const initialState: WizardState = {
  yardPostcode: "",
  radiusMiles: 10,
  defaultPrice: 150,
  districts: [],
  doesDropOff: false,
  dropOffPricePerTonne: null,
  sellAVehicleQuotesEnabled: false,
};

export default function Onboarding() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { forename } = useAuth();
  const { addNotification } = useNotification();

  const { status, saveCoverage, complete } = useOnboarding();
  const { update: updateCustomer } = useCustomer();
  const { priceSettings, updateConfig } = usePriceSettings();

  const [state, setState] = useState<WizardState>(initialState);
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string>();
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string>();
  const indexRef = useRef<DistrictIndex>();
  const prefilled = useRef(false);

  useEffect(() => {
    setInfo({ name: "Welcome to your dashboard", desc: "Let's get your yard set up in a few quick steps." });
  }, [setInfo]);

  // Preload the district index while the user is typing.
  useEffect(() => {
    loadDistrictIndex().then((i) => (indexRef.current = i)).catch(() => undefined);
  }, []);

  // Prefill from the API once (yard postcode from Betty, existing drop-off / SellAVehicle flags).
  useEffect(() => {
    const s = status.data?.data;
    if (!s || prefilled.current) return;
    prefilled.current = true;
    setState((prev) => ({
      ...prev,
      yardPostcode: s.yardPostcode ? normalisePostcode(s.yardPostcode) : prev.yardPostcode,
      radiusMiles: s.radiusMiles ?? prev.radiusMiles,
      doesDropOff: s.doesDropOff,
      dropOffPricePerTonne: s.dropOffPricePerTonne,
      sellAVehicleQuotesEnabled: s.sellAVehicleQuotesEnabled,
    }));
  }, [status.data]);

  // Already onboarded (e.g. deep link) -> nothing to do here.
  useEffect(() => {
    if (status.data?.data.onboardingCompleted) navigate("/", { replace: true });
  }, [status.data, navigate]);

  const goTo = useCallback((index: number) => {
    setStep(index);
    setMaxReached((m) => Math.max(m, index));
  }, []);

  // ---- Step 1: locate yard + build district list -------------------------------------------------------------
  const handleLocate = async (values: { yardPostcode: string; radiusMiles: number; defaultPrice: number }) => {
    setLocating(true);
    setLocateError(undefined);
    try {
      const geo = await geocodePostcode(values.yardPostcode);
      if (!geo) {
        setLocateError("We couldn't find that postcode. Please check it and try again.");
        return;
      }

      const index = indexRef.current ?? (indexRef.current = await loadDistrictIndex());
      const found = districtsWithinRadius(index, geo.latLng, values.radiusMiles, geo.outcode);

      if (found.length === 0) {
        setLocateError("No postcode districts were found in that radius. Try a larger radius.");
        return;
      }

      const towns = await lookupDistrictTowns(found.map((d) => d.district));

      // Keep any edits the user already made to districts that are still in range.
      const previous = new Map(state.districts.map((d) => [d.district, d]));
      const sameSetup = state.yardOutcode === geo.outcode && state.defaultPrice === values.defaultPrice;

      const districts: CoverageDistrictState[] = found.map((d) => {
        const prev = sameSetup ? previous.get(d.district) : undefined;
        return {
          district: d.district,
          area: d.area,
          town: towns[d.district],
          centre: d.centre,
          distanceMiles: d.distanceMiles,
          selected: prev?.selected ?? true,
          price: prev?.price ?? values.defaultPrice,
        };
      });

      setState((prev) => ({
        ...prev,
        yardPostcode: geo.postcode,
        yardOutcode: geo.outcode,
        yardLatLng: geo.latLng,
        radiusMiles: values.radiusMiles,
        defaultPrice: values.defaultPrice,
        districts,
      }));
      goTo(STEP.coverage);
    } catch (e: any) {
      setLocateError(e?.message ?? "Something went wrong looking up that postcode. Please try again.");
    } finally {
      setLocating(false);
    }
  };

  // ---- Step 2: coverage toggles ------------------------------------------------------------------------------
  const toggleDistrict = useCallback((district: string) => {
    setState((prev) => {
      const existing = prev.districts.find((d) => d.district === district);
      if (existing) {
        return {
          ...prev,
          districts: prev.districts.map((d) => (d.district === district ? { ...d, selected: !d.selected } : d)),
        };
      }
      // Clicked a grey "outside radius" district on the map: add it as selected.
      const index = indexRef.current;
      const area = district.match(/^[A-Z]+/)?.[0] ?? "";
      const entry = index?.areas[area]?.districts[district];
      if (!entry || !prev.yardLatLng) return prev;
      const [lat1, lng1] = prev.yardLatLng;
      const [lat2, lng2] = entry.c;
      const distanceMiles = Math.hypot((lat2 - lat1) * 69, (lng2 - lng1) * 69 * Math.cos((lat1 * Math.PI) / 180));
      return {
        ...prev,
        districts: [
          ...prev.districts,
          { district, area, centre: entry.c, distanceMiles, selected: true, price: prev.defaultPrice },
        ],
      };
    });
  }, []);

  const selectAll = useCallback((selected: boolean) => {
    setState((prev) => ({ ...prev, districts: prev.districts.map((d) => ({ ...d, selected })) }));
  }, []);

  // ---- Step 3: pricing ---------------------------------------------------------------------------------------
  const setPrice = useCallback((district: string, price: number) => {
    setState((prev) => ({
      ...prev,
      districts: prev.districts.map((d) => (d.district === district ? { ...d, price } : d)),
    }));
  }, []);

  const applyDefault = useCallback((price: number) => {
    setState((prev) => ({ ...prev, defaultPrice: price, districts: prev.districts.map((d) => ({ ...d, price })) }));
  }, []);

  // ---- Finish / skip -----------------------------------------------------------------------------------------
  const buildCoveragePayload = (): IOnboardingCoverageDistrict[] => {
    const index = indexRef.current;
    const chosen = new Map(state.districts.map((d) => [d.district, d]));
    const areas = Array.from(new Set(state.districts.filter((d) => d.selected).map((d) => d.area)));

    // Every district in every touched area: un-chosen ones are written as inactive so the group doesn't cover them.
    const all = index ? allDistrictsInAreas(index, areas) : Array.from(chosen.keys());

    return all.map((district) => {
      const d = chosen.get(district);
      return {
        postcode: district,
        town: d?.town,
        price: d?.selected ? d.price : state.defaultPrice,
        selected: d?.selected ?? false,
      };
    });
  };

  const handleFinish = async () => {
    setFinishing(true);
    setFinishError(undefined);
    try {
      const selectedCount = state.districts.filter((d) => d.selected).length;

      if (selectedCount > 0) {
        await saveCoverage.mutateAsync({
          yardPostcode: state.yardPostcode,
          radiusMiles: state.radiusMiles,
          defaultPrice: state.defaultPrice,
          districts: buildCoveragePayload(),
        });
      }

      const config = priceSettings.data?.data.customerPriceConfiguration;
      if (config && (config.doesDropOff !== state.doesDropOff || config.dropOffPricePerTonne !== state.dropOffPricePerTonne)) {
        await updateConfig.mutateAsync({
          ...config,
          doesDropOff: state.doesDropOff,
          dropOffPricePerTonne: state.doesDropOff ? state.dropOffPricePerTonne : null,
        });
      }

      if (status.data?.data.sellAVehicleQuotesEnabled !== state.sellAVehicleQuotesEnabled) {
        await updateCustomer.mutateAsync({ sellAVehicleQuotesEnabled: state.sellAVehicleQuotesEnabled });
      }

      await complete.mutateAsync(false);

      addNotification({ variant: "success", primaryText: "You're all set up!" });
      navigate("/", { replace: true });
    } catch (e: any) {
      setFinishError(e?.message ?? "Something went wrong saving your setup. Please try again.");
    } finally {
      setFinishing(false);
    }
  };

  const handleSkip = async () => {
    if (!window.confirm("Skip setup for now? You can set up postcodes and prices later under Postcode Prices.")) return;
    setFinishing(true);
    try {
      await complete.mutateAsync(true);
      navigate("/", { replace: true });
    } catch (e: any) {
      setFinishError(e?.message ?? "Couldn't skip setup, please try again.");
    } finally {
      setFinishing(false);
    }
  };

  const selectedCount = useMemo(() => state.districts.filter((d) => d.selected).length, [state.districts]);

  const canContinue = (() => {
    switch (step) {
      case STEP.coverage:
      case STEP.pricing:
        return selectedCount > 0;
      case STEP.dropoff:
        return !state.doesDropOff || state.dropOffPricePerTonne === null || state.dropOffPricePerTonne >= 0;
      default:
        return true;
    }
  })();

  if (status.isLoading) {
    return (
      <div className="min-h-[300px]">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="rounded-lg bg-white p-6 shadow">
        <p className="mb-6 text-sm text-gray-600">
          Hi{forename ? ` ${forename}` : ""}, welcome aboard. This takes about five minutes and gets your yard quoting
          straight away.
        </p>

        <div className="mb-8 sm:mb-14">
          <WizardStepper current={step} maxReached={maxReached} onSelect={goTo} />
        </div>

        <div className="min-h-[320px]">
          {step === STEP.location && (
            <LocationStep state={state} busy={locating} error={locateError} onSubmit={handleLocate} />
          )}
          {step === STEP.coverage && <CoverageStep state={state} onToggle={toggleDistrict} onSelectAll={selectAll} />}
          {step === STEP.pricing && <PricingStep state={state} onPriceChange={setPrice} onApplyDefault={applyDefault} />}
          {step === STEP.dropoff && (
            <DropOffStep state={state} onChange={(v) => setState((prev) => ({ ...prev, ...v }))} />
          )}
          {step === STEP.sellavehicle && (
            <SellAVehicleStep
              enabled={state.sellAVehicleQuotesEnabled}
              onChange={(sellAVehicleQuotesEnabled) => setState((prev) => ({ ...prev, sellAVehicleQuotesEnabled }))}
            />
          )}
          {step === STEP.review && <ReviewStep state={state} error={finishError} />}
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={handleSkip}
            disabled={finishing}
            className="text-sm text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
          >
            Skip setup for now
          </button>

          <div className="flex gap-3">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                disabled={finishing}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
            )}
            {step > STEP.location && step < STEP.review && (
              <ButtonInput isSubmit={false} label="Continue" onClick={() => goTo(step + 1)} disabled={!canContinue} />
            )}
            {step === STEP.review && (
              <ButtonInput
                isSubmit={false}
                label={finishing ? "Saving…" : "Finish setup"}
                onClick={() => void handleFinish()}
                disabled={finishing}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
